import { isEmpty, set } from 'lodash'

import BaseApi from '@/api/Base/BaseApi'
import {
  CreateDataCollectionEndpointPayload,
  DataCollectionCustomSchema,
  DataCollectionEndpoint,
  DataStorageCollectionInfo,
  DeleteAllCollectedDataPayload,
  DeleteCollectedDataPayload,
  GetCollectedDataListPayload,
  GetCollectedDataListResponse,
  UpdateDataCollectionCustomSchema,
  UpdateDataCollectionEndpointPayload
} from '@/api/DataCollection/types'

const ROOT = '/data-collection'

const defaultPaginationOptions = {
  itemsPerPage: 40,
  page: 0,
  sortDir: 'desc',
  sortKey: 'createdAt',
}

export default class DataCollectionApi extends BaseApi {
  createDataCollectionEndpoint(payload: CreateDataCollectionEndpointPayload) {
    const { workspaceId, ...rest } = payload
    return this.agent.post<DataCollectionEndpoint>(`${ROOT}/${workspaceId}/endpoint`, rest)
  }

  getDataCollectionEndpointList(workspaceId: string, hasImageField?: boolean) {
    return this.agent.get<DataCollectionEndpoint[]>(`${ROOT}/${workspaceId}/endpoint-list`, {
      params: hasImageField !== undefined ? { hasImageField } : undefined,
    })
  }

  getDataCollectionEndpoint(dataCollectionEndpointId: string) {
    return this.agent.get<DataCollectionEndpoint>(`${ROOT}/endpoint/${dataCollectionEndpointId}`)
  }

  getDataCollectionList(workspaceId: string) {
    return this.agent.get<DataStorageCollectionInfo[]>(`${ROOT}/${workspaceId}/collection-list`)
  }

  getDataCollectionInfo(dataCollectionInfoId: string) {
    return this.agent.get<DataStorageCollectionInfo>(`${ROOT}/collection-info/${dataCollectionInfoId}`)
  }

  getCollectedDataList(dataCollectionInfoId: string, options?: GetCollectedDataListPayload) {
    const {
      itemsPerPage,
      page,
      sortDir,
      sortKey,
      ...rest
    } = options ?? { ...defaultPaginationOptions }
    return this.agent.get<GetCollectedDataListResponse>(`${ROOT}/collection-info/${dataCollectionInfoId}/list`, {
      params: {
        page,
        num_per_page: itemsPerPage,
        sort_key: sortKey,
        sort_dir: sortDir,
        ...rest,
      },
    })
  }

  regenerateDataCollectionEndpoint(dataCollectionEndpointId: string) {
    return this.agent.put<DataCollectionEndpoint>(`${ROOT}/endpoint/${dataCollectionEndpointId}/regenerate-secret`)
  }

  updateDataCollectionEndpoint(payload: UpdateDataCollectionEndpointPayload) {
    const { dataCollectionEndpointId, ...rest } = payload
    return this.agent.put<DataCollectionEndpoint>(`${ROOT}/endpoint/${dataCollectionEndpointId}`, rest)
  }

  toggleActivateDataCollectionEndpoint(dataCollectionEndpointId: string) {
    return this.agent.post<DataCollectionEndpoint>(`${ROOT}/endpoint/${dataCollectionEndpointId}/toggle-active`)
  }

  deleteDataCollectionEndpoint(dataCollectionEndpointId: string) {
    return this.agent.delete<DataCollectionEndpoint>(`${ROOT}/endpoint/${dataCollectionEndpointId}`)
  }

  createCustomSchema(payload: CreateDataCollectionEndpointPayload) {
    const { workspaceId, ...rest } = payload
    return this.agent.post<DataCollectionCustomSchema>(`${ROOT}/${workspaceId}/custom-schema`, rest)
  }

  getCustomSchemaList(workspaceId: string) {
    return this.agent.get<DataCollectionCustomSchema[]>(`${ROOT}/${workspaceId}/custom-schema/list`)
  }

  updateCustomSchema(payload: UpdateDataCollectionCustomSchema) {
    const { customSchemaId, ...rest } = payload
    return this.agent.put(`${ROOT}/custom-schema/${customSchemaId}`, rest)
  }

  exportCSV(endpointId: string, startDate: string, endDate: string, selectedFields: string, ngFilters: string) {
    return this.agent.get<any>(`${ROOT}/collection-info/${endpointId}/export-csv`, {
      params: {
        startDate,
        endDate,
        selectedFields,
        ngFilters,
      },
      responseType: 'blob',
    })
  }

  deleteCollectedData(payload: DeleteCollectedDataPayload) {
    const { dataCollectionInfoId, dataIds } = payload
    return this.agent.delete(`${ROOT}/collection-info/${dataCollectionInfoId}/collected-data`, {
      data: {
        dataIds,
      },
    })
  }

  deleteAllCollectedData(payload: DeleteAllCollectedDataPayload) {
    const { dataCollectionInfoId, startDate, endDate, filters } = payload
    const data = {}
    if (startDate) {
      set(data, 'startDate', startDate)
    }
    if (endDate) {
      set(data, 'endDate', endDate)
    }
    if (!isEmpty(filters)) {
      set(data, 'filters', filters)
    }
    return this.agent.delete(`${ROOT}/collection-info/${dataCollectionInfoId}/all-collected-data`, {
      data,
    })
  }
}
